import { useEffect, useRef, useState } from "react";

// pdf.js is loaded on demand so the main bundle stays small. The worker ships in
// /public under a versioned name that must match the pdfjs-dist version in package.json.
const WORKER = `${process.env.PUBLIC_URL || ''}/pdfjs-3.11.174.worker.min.js`;
let pdfjsPromise = null;

export function loadPdfjs() {
  if (!pdfjsPromise) {
    pdfjsPromise = import("pdfjs-dist/build/pdf").then((lib) => {
      lib.GlobalWorkerOptions.workerSrc = WORKER;
      return lib;
    });
  }
  return pdfjsPromise;
}

/** Open a PDF from a File/Blob, ArrayBuffer, or URL. */
export async function openPdf(src) {
  const lib = await loadPdfjs();
  let data = src;
  if (typeof Blob !== "undefined" && src instanceof Blob) data = await src.arrayBuffer();
  if (typeof src === "string") {
    const res = await fetch(src);
    if (!res.ok) throw new Error(`Document fetch failed (${res.status})`);
    data = await res.arrayBuffer();
  }
  return lib.getDocument({ data: new Uint8Array(data) }).promise;
}

/** Default field box per type, as fractions of the displayed page. Mirrors backend common/esign_stamp.py. */
export const FIELD_SIZE = {
  signature: { w: 0.26, h: 0.05 },
  initial: { w: 0.08, h: 0.04 },
  date: { w: 0.16, h: 0.03 },
  text: { w: 0.26, h: 0.03 },
};

export const RECIPIENT_COLORS = ["#D2542B", "#2F6DB5", "#4A6B3C", "#8A4FB0", "#B8741F", "#1F8A8A"];

export function initialsOf(name) {
  return (name || "").replace(/-/g, " ").split(/\s+/).filter((p) => /^[A-Za-z]/.test(p)).slice(0, 3).map((p) => p[0].toUpperCase()).join("");
}

function PdfPage({ pdf, pageNo, width, children, onClick, pageRef }) {
  const canvasRef = useRef(null);
  const [size, setSize] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let task = null;
    pdf.getPage(pageNo).then((page) => {
      if (cancelled) return;
      const base = page.getViewport({ scale: 1 });
      const scale = width / base.width;
      const vp = page.getViewport({ scale });
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = Math.floor(vp.width * ratio);
      canvas.height = Math.floor(vp.height * ratio);
      canvas.style.width = `${vp.width}px`;
      canvas.style.height = `${vp.height}px`;
      setSize({ w: vp.width, h: vp.height });
      task = page.render({ canvasContext: canvas.getContext("2d"), viewport: vp, transform: ratio !== 1 ? [ratio, 0, 0, ratio, 0, 0] : null });
      task.promise.catch(() => {});
    });
    return () => { cancelled = true; if (task) task.cancel(); };
  }, [pdf, pageNo, width]);

  return (
    <div ref={pageRef} data-page={pageNo} onClick={onClick}
      style={{ position: "relative", width: size ? size.w : width, height: size ? size.h : width * 1.294, background: "#fff", boxShadow: "0 1px 3px rgba(20,17,14,0.18)", margin: "0 auto", touchAction: "manipulation" }}>
      <canvas ref={canvasRef} style={{ display: "block" }} />
      {size && children(size)}
    </div>
  );
}

/**
 * Renders every page of `pdf` at `width` px. `renderOverlay(pageNo, {w,h})` draws on top of each page;
 * `onPageClick(pageNo, fx, fy)` reports clicks as fractions of the displayed page (top-left origin).
 */
export function PdfPages({ pdf, width, renderOverlay, onPageClick }) {
  const pages = Array.from({ length: pdf?.numPages || 0 }, (_, i) => i + 1);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {pages.map((n) => (
        <div key={n}>
          <div style={{ fontSize: 12, color: "#8F8678", textAlign: "center", marginBottom: 4 }}>Page {n} of {pages.length}</div>
          <PdfPage pdf={pdf} pageNo={n} width={width}
            onClick={onPageClick ? (e) => {
              if (e.target.closest("[data-field]")) return;
              const r = e.currentTarget.getBoundingClientRect();
              onPageClick(n, (e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height);
            } : undefined}>
            {(size) => renderOverlay ? renderOverlay(n, size) : null}
          </PdfPage>
        </div>
      ))}
    </div>
  );
}

/** Width of a container, tracked on resize. Returns a callback ref so late-mounted nodes are measured. */
export function useContainerWidth(fallback = 560) {
  const [el, setEl] = useState(null);
  const [w, setW] = useState(fallback);
  useEffect(() => {
    if (!el) return undefined;
    const measure = () => setW(Math.max(240, Math.floor(el.clientWidth)));
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);
  return [setEl, w];
}
